<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('luggage.tracking.subtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <div class="sv-tabs">
          <button
            type="button"
            class="sv-tab"
            :class="{ 'sv-tab--active': activeTab === 0 }"
            @click="activeTab = 0"
          >
            <v-icon small>mdi-ticket-outline</v-icon>
            {{ $t('luggage.tracking.byTicket') }}
          </button>
          <button
            type="button"
            class="sv-tab"
            :class="{ 'sv-tab--active': activeTab === 1 }"
            @click="activeTab = 1"
          >
            <v-icon small>mdi-routes</v-icon>
            {{ $t('luggage.tracking.byProgram') }}
          </button>
        </div>
      </div>
    </div>

    <div class="ov-stack">
      <!-- ===== Recherche par billet (tab 0) ===== -->
      <div v-show="activeTab === 0" class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            <v-icon small color="#159e72">mdi-magnify</v-icon>
            {{ $t('luggage.tracking.searchByTicketTitle') }}
          </div>
        </div>
        <div class="ov-card__body">
          <div class="sv-search">
            <div class="ov-field sv-search__field">
              <label class="ov-label" for="sv-reference">
                {{ $t('luggage.ticketReference') }}<span class="ov-required">*</span>
              </label>
              <div class="ov-search sv-search__input">
                <v-icon small>mdi-ticket-confirmation-outline</v-icon>
                <input
                  id="sv-reference"
                  v-model.trim="referenceTicket"
                  type="text"
                  class="ov-input"
                  :class="{ 'is-invalid': referenceMissing }"
                  :placeholder="$t('luggage.tracking.ticketReferencePlaceholder')"
                  @input="referenceMissing = false"
                  @keyup.enter="getBagsByReference"
                />
              </div>
              <div v-if="referenceMissing" class="ov-error">{{ $t('luggage.tracking.referenceRequired') }}</div>
            </div>
            <button
              type="button"
              class="ov-btn ov-btn--primary"
              :disabled="isSearchingByTicket"
              @click="getBagsByReference"
            >
              <v-progress-circular v-if="isSearchingByTicket" indeterminate size="16" width="2"></v-progress-circular>
              <v-icon v-else small>mdi-magnify</v-icon>
              {{ $t('luggage.search') }}
            </button>
          </div>
        </div>
      </div>

      <!-- ===== Offres de voyage (tab 1) ===== -->
      <div v-show="activeTab === 1" class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            {{ $t('luggage.tracking.travelOffersList') }}
            <span class="ov-count">{{ offreVoyageDisponibleList.length }}</span>
          </div>
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input
              v-model="search"
              type="text"
              class="ov-input"
              :placeholder="$t('luggage.tracking.searchPlaceholder')"
            />
          </div>
        </div>
        <v-data-table
          :headers="headers"
          :items="offreVoyageDisponibleList"
          :loading="loading"
          loader-height="2"
          :search="search"
          class="ov-table"
        >
          <template v-slot:[`item.designation`]="{ item }">
            <span class="sv-strong">{{ item.designation }}</span>
          </template>
          <template v-slot:[`item.isActif`]="{ item }">
            <span class="ov-badge" :class="item.isActif ? 'ov-badge--success' : 'ov-badge--neutral'">
              {{ item.isActif ? $t('luggage.tracking.active') : $t('luggage.tracking.inactive') }}
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <button
              type="button"
              class="ov-btn ov-btn--primary ov-btn--sm"
              @click="getPlanningByOffersTravel(item.designation)"
            >
              <v-icon small>mdi-cursor-default-click-outline</v-icon>
              {{ $t('luggage.tracking.select') }}
            </button>
          </template>
        </v-data-table>
      </div>

      <!-- ===== TAB 0 : résultats par billet ===== -->
      <div v-show="activeTab === 0">
        <div v-if="apiResponse" class="ov-stack">
          <div v-if="!typeStrategieCompagnieStrategie" class="ov-note ov-note--warning">
            <v-icon small color="#92400e">mdi-alert-outline</v-icon>
            <span>{{ $t('luggage.tracking.noStrategy') }}</span>
          </div>

          <DetailBagageClient
            v-if="typeStrategieCompagnieStrategie == 'typeStrategieBagagePoids'"
            :response="apiResponse"
            :strategyType="typeStrategieCompagnieStrategie"
            :ticketReference="referenceTicket"
          ></DetailBagageClient>

          <DetailBagageClientType
            v-else-if="typeStrategieCompagnieStrategie == 'typeStrategieBagageType'"
            :response="apiResponse"
            :strategyType="typeStrategieCompagnieStrategie"
            :ticketReference="referenceTicket"
          ></DetailBagageClientType>

          <div v-else class="ov-note">
            <v-icon small>mdi-information-outline</v-icon>
            <span>{{ $t('luggage.tracking.unknownStrategy') }}: {{ typeStrategieCompagnieStrategie }}</span>
          </div>
        </div>
      </div>

      <!-- ===== TAB 1 : résultats par programme ===== -->
      <div v-show="activeTab === 1" class="ov-stack">
        <!-- Programmes -->
        <div v-if="programmeList.length > 0" class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">
              {{ $t('luggage.tracking.availablePrograms') }}
              <span class="ov-count">{{ programmeList.length }}</span>
            </div>
          </div>
          <div class="ov-card__body sv-programs">
            <div v-for="(program, p) in programmeList" :key="p" class="sv-program">
              <div class="ov-card__kicker">{{ $t('luggage.tracking.program') }} {{ p + 1 }}</div>
              <div class="sv-program__name">{{ program.designation }}</div>
              <button
                type="button"
                class="ov-btn ov-btn--secondary ov-btn--sm ov-btn--block"
                @click="getBagsByOffre(program.designation)"
              >
                <v-icon small>mdi-bag-suitcase-outline</v-icon>
                {{ $t('luggage.tracking.viewBags') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Résultats par programme -->
        <div v-if="apiResponseProgramme !== null" class="ov-grid">
          <div class="ov-card">
            <div class="ov-card__head">
              <div class="ov-card__title">{{ $t('luggage.tracking.bagDetails') }}</div>
            </div>
            <v-data-table
              v-if="typeStrategieCompagnieStrategie == 'typeStrategieBagagePoids'"
              :headers="headersTwo"
              :items="apiResponseProgramme || []"
              :loading="loadingTwo"
              loader-height="2"
              :search="searchTwo"
              class="ov-table"
            ></v-data-table>
            <v-data-table
              v-else
              :headers="headersOne"
              :items="apiResponseProgramme || []"
              :loading="loadingOne"
              loader-height="2"
              :search="searchOne"
              class="ov-table"
            ></v-data-table>
          </div>

          <aside class="ov-card ov-sticky">
            <div class="ov-card__head">
              <div class="ov-card__title">{{ $t('luggage.tracking.summary') }}</div>
            </div>
            <div class="ov-card__body">
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('luggage.tracking.totalBags') }}</span>
                <span class="ov-stat__value">
                  {{
                    typeStrategieCompagnieStrategie == 'typeStrategieBagagePoids'
                      ? getTotalBagagePoids
                      : getTotalBagage
                  }}
                </span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('luggage.tracking.totalAmount') }}</span>
                <span class="ov-stat__value ov-stat__value--accent">
                  {{ getTotalMontantSoldable.toLocaleString('fr-FR') }} FCFA
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import {
  API_FIND_BAGS_BY_OFFRE,
  API_FIND_BAGS_BY_TICKET,
  API_GET_RESERVATIONS_BY_ADMIN_TP,
  API_GET_RESERVATIONS_BY_SELLER,
  API_GET_STRATEGIE_PAR_COMPAGNIE,
  API_GET_TICKET_BY_REFERENCE,
  API_RECUPERER_LISTE_OFFRE_VOYAGE,
  API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import axios from "axios";
import DetailBagageClient from "./widgets/DetailBagageClient.vue";
import DetailBagageClientType from "./widgets/DetailBagageClientType.vue";
import { showErrorMessage } from "@/modules/messages/messageProcess";
export default {
  name: "SuiviBagages",
  components: {
    DetailBagageClient,
    DetailBagageClientType,
  },
  data() {
    return {
      activeTab: 0,
      searchType: "referenceBilletReservation",
      referenceMissing: false,
      isSearchingByTicket: false,
      isSearchingByDesignation: false,
      errorMsg: null,
      successMsg: null,

      programSelected: null,

      loading: true,

      offreVoyageDisponibleList: [],
      programmeList: [],

      offreVoyageProgramObject: {
        data: {
          designation: null,
        },
      },

      offreVoyageObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },

      search: "",
      headers: [
        { text: "reference", value: "id" },
        { text: "Designation", value: "designation" },
        { text: "Type de l'offre", value: "typeOffreVoyageDesignation" },
        { text: "Ville de départ", value: "villeDepartDesignation" },
        { text: "Ville d'arrivée", value: "villeDestinationDesignation" },
        { text: "Active", value: "isActif" },
        { text: "Actions", value: "actions", sortable: false },
      ],

      searchOne: "",
      loadingTwo: false,
      headersOne: [
        { text: "N° billet", value: "reservationBilletReference" },
        { text: "Description", value: "designation" },
        { text: "Montant à payer(CFA)", value: "montantSoldable" },
        {
          text: "Total bagages",
          value: "bagageTypeDTO.bagageTypeReferenceDTOS.length",
        },
      ],

      searchTwo: "",
      loadingOne: false,
      headersTwo: [
        { text: "reference", value: "reservationBilletReference" },
        { text: "Description", value: "designation" },
        { text: "Montant à payer(CFA)", value: "montantSoldable" },
        {
          text: "Total bagages",
          value: "bagagePoidsDTO.bagagePoidsReferenceDTOS.length",
        },
      ],

      referenceTicket: null,

      designationProgramme: null,

      bagageByTicket: {
        data: {
          reservationBilletReference: null,
        },
      },

      ticketByRefObject: {
        data: {
          designation: null,
        },
      },

      bagageByProgramme: {
        data: {
          designation: null,
        },
      },

      ticketAvailableList: [],

      objectToSend: {
        data: {},
      },

      apiResponse: null,
      apiResponseProgramme: null,

      strategieObject: {
        data: {
          compagnieTransportRaisonSociale: this.$store.state.userAuthentified.compagnieTransportRaisonSociale
        },
      },

      typeStrategieCompagnieStrategie: null,
    };
  },

  watch: {
    activeTab(newVal) {
      this.searchType =
        newVal === 0 ? "referenceBilletReservation" : "designationProgramme";
      if (newVal === 0) {
        this.apiResponseProgramme = null;
      } else {
        this.apiResponse = null;
      }
    },
  },

  methods: {
    async searchTicketByReference() {
      //this.isSearching = true;
      if (!this.referenceTicket) {
        this.errorMsg = this.$t('luggage.tracking.enterReference');
        showErrorMessage();
      } else {
        const found = this.ticketAvailableList.find(
          (element) => element.designation == this.referenceTicket
        );
        if (found) {
          this.$store.commit("STORE_TICKET", found);
        } else {
          this.errorMsg = this.$t('luggage.tracking.noMatch');
          showErrorMessage();
        }
      }
      //this.isSearching = false;
    },

    //RÉCUPÉRER LA LISTE DES BILLETS DISPONIBLES.
    async getTicketAvailables() {
      await axios
        .post(
          this.$store.state.userAuthentified.roleCode ==
            "RoleUtiGareCompagnieTransport"
            ? API_GET_RESERVATIONS_BY_SELLER
            : API_GET_RESERVATIONS_BY_ADMIN_TP,
          this.objectToSend,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          }
        )
        .then((response) => {
          if (response.status == 200) {
            if (response.data.hasError == false) {
              this.ticketAvailableList = response.data.items;
            }
          }
        })
        .catch((e) => {
          this.errorMsg = e;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    //OBTENIR LA LISTE DES PROGRAMMES PAR OFFRE DE VOYAGE
    async getPlanningByOffersTravel(designationOffreVoyage) {
      this.offreVoyageProgramObject.data.designation = designationOffreVoyage;
      await axios
        .post(
          API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
          this.offreVoyageProgramObject,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.data.status.code == 800) {
            this.programmeList = response.data.items;
          } else {
            this.programmeList = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          this.programmeList = [];
          showErrorMessage()
        });
    },

    // OBTENIR LA LISTE DES OFFRES DE VOYAGE DISPONIBLE
    async obtenirOffreVoyageListParCompagnie() {
      this.offreVoyageObject.data.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.loading = true;
      await axios
        .post(API_RECUPERER_LISTE_OFFRE_VOYAGE, this.offreVoyageObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage()
            } else {
              this.offreVoyageDisponibleList = response.data.items;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage()
          }
        })
        .catch((e) => {
          this.errorMsg = e;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Rechercher bagages par reference billet de réservation
    async getBagsByReference() {
      // Pas d'appel API sans référence
      if (!this.referenceTicket) {
        this.referenceMissing = true;
        return;
      }
      this.referenceMissing = false;
      this.apiResponseProgramme = null;
      this.isSearchingByTicket = true;
      this.bagageByTicket.data.reservationBilletReference =
        this.referenceTicket;
      await axios
        .post(API_FIND_BAGS_BY_TICKET, this.bagageByTicket, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.apiResponse = response.data.item;
            this.fetchClientByRef(this.referenceTicket);
          } else {
            this.apiResponse = null;
            this.errorMsg = this.$t('luggage.tracking.noResponse');
            showErrorMessage()
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage()
        })
        .finally(() => {
          this.isSearchingByTicket = false;
        });
    },

    // Récupérer les infos client via la référence billet
    async fetchClientByRef(reference) {
      this.ticketByRefObject.data.designation = reference;
      await axios
        .post(API_GET_TICKET_BY_REFERENCE, this.ticketByRefObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          const code = String(response?.data?.status?.code || "");
          if (response.status === 200 && code === "800") {
            this.$store.commit("STORE_TICKET", response.data.item);
          }
        })
        .catch((e) => {
          console.error("fetchClientByRef error:", e);
        });
    },

    // Rechercher bagages par programme designation
    async getBagsByOffre(designationProgram) {
      this.isSearchingByDesignation = true;
      this.loadingOne = true;
      this.apiResponse = null;
      this.bagageByProgramme.data.designation = designationProgram;
      await axios
        .post(API_FIND_BAGS_BY_OFFRE, this.bagageByProgramme, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.apiResponseProgramme = response.data.items;
          } else {
            this.apiResponseProgramme = null;
            this.errorMsg = this.$t('luggage.tracking.noData');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.isSearchingByDesignation = false;
          this.loadingOne = false;
        });
    },

    // Obtenir la strategie de gestion de bagage de la compagnie
    async getStrategyByCompany() {
      await axios
        .post(API_GET_STRATEGIE_PAR_COMPAGNIE, this.strategieObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.typeStrategieCompagnieStrategie = null;
            } else {
              this.typeStrategieCompagnieStrategie =
                response.data.item.typeStrategieBagageDesignation;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage()
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage()
        });
    },
  },

  computed: {
    getTotalBagage() {
      var totalBagage = 0;
      if (this.apiResponseProgramme == null) return 0;
      
      for (let i = 0; i < this.apiResponseProgramme.length; i++) {
        const bagageDTO = this.apiResponseProgramme[i].bagageTypeDTO;
        if (bagageDTO && bagageDTO.bagageTypeReferenceDTOS) {
          // Compter tous les bagages (avec doublons)
          totalBagage += bagageDTO.bagageTypeReferenceDTOS.length;
        }
      }
      return totalBagage;
    },

    getTotalBagagePoids() {
      var totalBagage = 0;
      if (this.apiResponseProgramme == null) return 0;
      
      for (let i = 0; i < this.apiResponseProgramme.length; i++) {
        const bagageDTO = this.apiResponseProgramme[i].bagagePoidsDTO;
        if (bagageDTO && bagageDTO.bagagePoidsReferenceDTOS) {
          // Compter tous les bagages (avec doublons)
          totalBagage += bagageDTO.bagagePoidsReferenceDTOS.length;
        }
      }
      return totalBagage;
    },

    getTotalMontantSoldable() {
      var totalMontantSoldable = 0;
      if (this.apiResponseProgramme == null) return (totalMontantSoldable = 0);
      for (let i = 0; i < this.apiResponseProgramme.length; i++) {
        totalMontantSoldable += this.apiResponseProgramme[i].montantSoldable;
      }
      return totalMontantSoldable;
    },
  },

  mounted() {
    this.getStrategyByCompany();
    this.obtenirOffreVoyageListParCompagnie();
    this.getTicketAvailables();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/admin/ui";

/* Onglets billet / programme */
.sv-tabs {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
}

.sv-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $ov-muted;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  .v-icon {
    color: inherit !important;
  }

  &--active {
    background: #ffffff;
    color: $ov-green;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  }
}

/* Recherche par référence */
.sv-search {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.sv-search__field {
  flex: 1;
}

.sv-search__input .ov-input {
  height: 44px;
}

.sv-strong {
  font-weight: 700;
  color: $ov-navy;
}

/* Programmes */
.sv-programs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.sv-program {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border: 1px solid $ov-line;
  border-radius: 12px;
  background: $ov-soft;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.4);
  }
}

.sv-program__name {
  flex: 1;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: $ov-navy;
  word-break: break-word;
}

@media (max-width: 760px) {
  .sv-search {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
